import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import cs from "classnames";
import "components/organisms/table/Table.scss";
import { CSSProperties, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PiPlus } from "react-icons/pi";
import { ISize } from "types/components/components.interface";
import { measuring } from "utils/dnd/dndUtilities";
import TableDraggableSortableRow, {
  ITableDraggableColumns,
} from "../sortable-item/TableDraggableSortableRow";
import "./TableDraggableSortableList.scss";

export interface ITableDraggableSortableListProps<
  T extends { id: UniqueIdentifier }
> extends ISize {
  onRowClick?: (id: string) => void;
  displayMode?: boolean;
  loading?: boolean;
  data: { nodes: T[] };
  columns: ITableDraggableColumns<T>[];
  handleDragEnd: (event: DragEndEvent) => void;
  onAdd?: () => void;
}

const TableDraggableSortableList = <T extends { id: UniqueIdentifier }>({
  columns,
  data,
  displayMode,
  handleDragEnd,
  loading,
  onRowClick,
  onAdd,
  size,
}: ITableDraggableSortableListProps<T>) => {
  // const [activeItem, setActiveItem] = useState<T | null>(null);

  const { t } = useTranslation();

  const sortedOptionsIds = useMemo(
    () => data.nodes.map((row) => row.id),
    [data.nodes]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: "0.8",
        },
      },
    }),
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    const item = data.nodes.find((node) => node.id === active.id);
    console.log(active.id, item);
    // setActiveItem(item ?? null);
    document.body.style.setProperty("cursor", "grabbing");
  };

  const resetState = () => {
    // setActiveItem(null);
    document.body.style.setProperty("cursor", "");
  };

  const draggable = !displayMode;

  const tableClassName = cs("table-draggable", {
    draggable,
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={resetState}
    >
      <div
        className={tableClassName}
        style={{ "--table-draggable-columns": columns.length } as CSSProperties}
      >
        <div className="table-draggable-header">
          <div className="table-draggable-header-row">
            {draggable && <div className="table-draggable-header-cell" />}
            {columns.map((column, key) => (
              <div className="table-draggable-header-cell" key={key}>
                {column.label}
              </div>
            ))}
          </div>
        </div>

        <div className="table-draggable-body">
          {displayMode && !data.nodes.length && (
            <div className="table-draggable-additional-row">
              {t("table.noData")}
            </div>
          )}
          <SortableContext
            items={sortedOptionsIds}
            strategy={verticalListSortingStrategy}
          >
            {data.nodes.map((item, key) => (
              <TableDraggableSortableRow
                key={key}
                item={item}
                columns={columns}
                draggable={draggable}
              />
            ))}
          </SortableContext>
          {onAdd && !displayMode && (
            <div
              onClick={onAdd}
              className="table-draggable-additional-row selectable"
            >
              <div>{t("button.add")}</div>
              <PiPlus />
            </div>
          )}
        </div>
      </div>
    </DndContext>
  );
};

export default TableDraggableSortableList;
